import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Modal, Tabs, Tooltip, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import { deleteHealthClient, fetchHealthClientUsl } from "./health-clients-api";
import Client, { CLIENT_TAB_ITEMS, CLIENT_TAB_KEYS } from "./client";
import xSrc from "./assets/x.svg";
import "./client.css";

const DRAWER_WIDTH_KEY = "planOnline.health.clients.clientDrawer.width.v2";
const MIN_WIDTH = 800;
const MAX_WIDTH = 1200;
const DEFAULT_WIDTH = 980;

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const ClientDrawer = ({ open, idClient, isNew = false, onClose, onSaved, onDeleted }) => {
  const [size, setSize] = useState(loadWidth);
  const [activeTab, setActiveTab] = useState(CLIENT_TAB_KEYS.MAIN);
  const [canDelete, setCanDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && idClient && !isNew) {
      fetchHealthClientUsl(idClient)
        .then((list) => setCanDelete((Array.isArray(list) ? list : []).length === 0))
        .catch(() => setCanDelete(false));
    } else {
      setCanDelete(null);
    }
  }, [open, idClient, isNew, activeTab]);

  useEffect(() => {
    if (open) setActiveTab(CLIENT_TAB_KEYS.MAIN);
  }, [open, idClient, isNew]);

  useEffect(() => {
    if (isNew && activeTab !== CLIENT_TAB_KEYS.MAIN) {
      setActiveTab(CLIENT_TAB_KEYS.MAIN);
    }
  }, [isNew, activeTab]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const tabItems = useMemo(
    () =>
      CLIENT_TAB_ITEMS.map((item) => ({
        ...item,
        disabled: isNew && item.key !== CLIENT_TAB_KEYS.MAIN
      })),
    [isNew]
  );

  const onTabChange = useCallback(
    (key) => {
      if (isNew && key !== CLIENT_TAB_KEYS.MAIN) return;
      setActiveTab(key);
    },
    [isNew]
  );

  const title = (
    <div className="client-drawer__title">
      <Tabs
        className="client-drawer__tabs"
        activeKey={activeTab}
        onChange={onTabChange}
        items={tabItems}
      />
    </div>
  );

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={{ placement: "end" }}
      rootClassName="client-drawer"
      closeIcon={
        <span className="client-drawer__close">
          <img src={xSrc} alt="" />
        </span>
      }
      extra={
        <div className="client-drawer__extra">
          <div className="client-drawer__print">
            <Tooltip title={canDelete === false ? "Удаление недоступно: у клиента есть назначения" : "Удалить клиента"}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                className="client-drawer__delete"
                disabled={isNew || idClient == null || !canDelete || deleting}
                loading={deleting}
                onClick={() =>
                  Modal.confirm({
                    title: "Удалить клиента?",
                    content:
                      "Карточка клиента лечебной части будет удалена вместе с историей болезни, дневником и диагнозами.",
                    okText: "Удалить",
                    okButtonProps: { danger: true },
                    cancelText: "Отмена",
                    onOk: async () => {
                      setDeleting(true);
                      try {
                        await deleteHealthClient(idClient);
                        message.success("Клиент удалён");
                        (onDeleted || onClose)?.(idClient);
                      } catch (e) {
                        message.error(e.message || "Не удалось удалить клиента");
                      } finally {
                        setDeleting(false);
                      }
                    }
                  })
                }
              />
            </Tooltip>
          </div>
          <div className="client-drawer__print">
            <ReportButton
              vid={REPORT_VID.CLIENTS}
              id={idClient}
              compact
              disabled={isNew || idClient == null}
            />
          </div>
        </div>
      }
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idClient || isNew) ? (
        <Client
          idClient={idClient}
          isNew={isNew}
          open={open}
          activeTab={activeTab}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default ClientDrawer;
